import type {
  AnnulOperation,
  BranchChangeset,
  ConfigurationValidationError,
} from '@festina/configuration-general-types/configuration'
import { OperationLayerMap, type WorkspaceKey } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import type { ConfigurationItemsByLayer } from '../../workspace/operationsContext'
import type { ValidationErrorWithLocation } from '../errors/types'
import type { WorkspaceOperationOf } from '../types'
import type { ConfiguredEnumLiteral } from './types'
import { useConfiguredEnumLiteralsFromChangeset } from './useConfiguredEnumLiteralsFromChangeset'
import { useDraftEnumLiterals } from './useDraftEnumLiterals'

export const useConfiguredEnumLiterals = ({
  configurationChangesets,
  workspaceKey,
  errors,
  draftAnnulOperations,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  workspaceKey: Optional<WorkspaceKey>
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[]
  draftAnnulOperations: WorkspaceOperationOf<AnnulOperation>[]
}): Map<string, ConfigurationItemsByLayer<ConfiguredEnumLiteral[]>> => {
  const draftLiterals = useDraftEnumLiterals(workspaceKey, errors)
  const changesetLiterals = useConfiguredEnumLiteralsFromChangeset({
    configurationChangesets,
    errors,
    draftAnnulOperations,
  })

  return useMemo(() => {
    const indexedLiteralsMap = new Map<
      string,
      ConfigurationItemsByLayer<ConfiguredEnumLiteral[]>
    >()

    Array.from(changesetLiterals.entries()).forEach(
      ([block, changesetLayerMap]) => {
        Array.from(changesetLayerMap.entries()).forEach(
          ([layer, layerLiterals]) => {
            const indexedBlock =
              indexedLiteralsMap.get(block) ??
              new OperationLayerMap<Map<string, ConfiguredEnumLiteral[]>>()
            indexedLiteralsMap.set(block, indexedBlock)

            const indexedLiteralsForLayer =
              indexedBlock.get(layer) ??
              new Map<string, ConfiguredEnumLiteral[]>()
            indexedBlock.set(layer, indexedLiteralsForLayer)

            layerLiterals.forEach((literal) => {
              const enumName = literal.internalOperation.entity?.enumName
              const literalName = literal.internalOperation.entity?.name

              if (enumName && literalName) {
                const literalKey = `${enumName}.${literalName}`
                const enumLiterals =
                  indexedLiteralsForLayer.get(literalKey) ?? []
                indexedLiteralsForLayer.set(literalKey, enumLiterals)

                enumLiterals.push(literal)
              }
            })
          },
        )
      },
    )

    // Draft literals take precedence over changeset literals. If there's matching draft and changeset literals, the draft literal is pushed to the end
    // taking precedence over other literals in the enum literal viewer when the application view is created.
    draftLiterals.forEach((draftLiteral) => {
      const enumName = draftLiteral.internalOperation.entity?.enumName
      const literalName = draftLiteral.internalOperation.entity?.name

      const draftLiteralBlock = draftLiteral.block
      const draftLiteralBlockMap =
        indexedLiteralsMap.get(draftLiteralBlock) ??
        new OperationLayerMap<Map<string, ConfiguredEnumLiteral[]>>()
      indexedLiteralsMap.set(draftLiteralBlock, draftLiteralBlockMap)

      const draftLiteralsMapForLayer =
        draftLiteralBlockMap.get(draftLiteral.layer) ??
        new Map<string, ConfiguredEnumLiteral[]>()
      draftLiteralBlockMap.set(draftLiteral.layer, draftLiteralsMapForLayer)

      if (enumName && literalName) {
        const literalKey = `${enumName}.${literalName}`
        const literalsOnLayer = draftLiteralsMapForLayer.get(literalKey) ?? []
        draftLiteralsMapForLayer.set(literalKey, literalsOnLayer)

        assertNotEmpty(draftLiteral.internalOperation.entity)
        literalsOnLayer.push(draftLiteral)
      }
    })

    return indexedLiteralsMap
  }, [changesetLiterals, draftLiterals])
}
